import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../BaseComponents/Button';
import PanelsTable from './PanelsTable';
import PanelFormModal from './PanelFormModal';
import { ViewPanelModal, DeletePanelModal } from './ViewPanelModal';
import { getPanelsList, PanelWithDetails } from '../../../api/panels';
import './PanelsTab.scss';

const PanelsTab = () => {
  const [panelsList, setPanelsList] = useState<PanelWithDetails[]>([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingPanel, setEditingPanel] = useState<PanelWithDetails | null>(null);
  const [viewingPanel, setViewingPanel] = useState<PanelWithDetails | null>(null);
  const [deletingPanel, setDeletingPanel] = useState<PanelWithDetails | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortingLoading, setSortingLoading] = useState<boolean>(false);

  const fetchPanels = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getPanelsList(searchQuery, 50, 0, sortField, sortOrder);
      setPanelsList(response.panels || []);
    } catch (error) {
      console.error('Error fetching panels:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, sortField, sortOrder]);

  useEffect(() => {
    fetchPanels();
  }, [fetchPanels]);

  const onAddPanelBtnClick = () => {
    setEditingPanel(null);
    setIsFormModalOpen(true);
  };

  const onViewPanel = (panel: PanelWithDetails) => {
    setViewingPanel(panel);
    setIsViewModalOpen(true);
  };

  const onEditPanel = (panel: PanelWithDetails) => {
    setEditingPanel(panel);
    setIsFormModalOpen(true);
  };

  const onCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingPanel(null);
  };

  const onCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingPanel(null);
  };

  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingPanel(null);
  };

  const onPanelSaved = async () => {
    await fetchPanels();
    setIsFormModalOpen(false);
    setEditingPanel(null);
  };

  const onDeletePanel = (panelId: number) => {
    const panel = panelsList.find(p => p.id === panelId);
    if (panel) {
      setDeletingPanel(panel);
      setIsDeleteModalOpen(true);
    }
  };

  const onPanelDeleted = async () => {
    await fetchPanels();
    setIsDeleteModalOpen(false);
    setIsViewModalOpen(false);
    setIsFormModalOpen(false);
    setDeletingPanel(null);
    setViewingPanel(null);
    setEditingPanel(null);
  };

  const handleSort = async (field: string) => {
    const apiSortField = field === 'body_system' ? 'created_at' : field;
    
    let newOrder: 'asc' | 'desc' = 'asc';
    if (sortField === apiSortField && sortOrder === 'asc') {
      newOrder = 'desc';
    }
    
    setSortField(apiSortField);
    setSortOrder(newOrder);
    
    setSortingLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setSortingLoading(false);
  };

  return (
    <div className='panels-tab'>
      <div className='search-row-container'>
        <div className='search-and-filter-container d-flex align-items-center gap-20'>
          <div className="search-input-container">
            <input
              type="text" 
              className="search-input"
              placeholder="Search Panels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className='count-container'>
            {panelsList.length === 1 ? 'Panel' : 'Panels'} ({panelsList.length})
          </div>
        </div>
        <Button
          isSecondary={true}
          text='Add Panel'
          onClick={onAddPanelBtnClick}
        />
      </div>

      <div className='panels-container'>
        <PanelsTable 
          panelsList={panelsList} 
          isLoading={isLoading}
          sortingLoading={sortingLoading}
          onViewPanel={onViewPanel}
          onEditPanel={onEditPanel}
          onDeletePanel={onDeletePanel}
          onSort={handleSort}
        />
      </div>

      {isFormModalOpen && (
        <PanelFormModal
          isOpen={isFormModalOpen}
          onClose={onCloseFormModal}
          panelToEdit={editingPanel}
          onPanelSaved={onPanelSaved}
        />
      )}

      {isViewModalOpen && (
        <ViewPanelModal
          isOpen={isViewModalOpen}
          onClose={onCloseViewModal}
          panel={viewingPanel}
          onEdit={onEditPanel}
          onDelete={onDeletePanel}
        />
      )}

      {isDeleteModalOpen && (
        <DeletePanelModal
          isOpen={isDeleteModalOpen}
          onClose={onCloseDeleteModal}
          panelToDelete={deletingPanel}
          onPanelDeleted={onPanelDeleted}
        />
      )}
    </div>
  );
};

export default PanelsTab;

